import {
  html,
  css,
  unsafeHTML,
  ref,
  createRef,
  paths,
  t,
  styles,
} from "@hh/global.js"

class HHDraggableSlider extends customElements.get("hh-slider") {
  static properties = {
    active: {
      reflect: true,
    },
    breakpoint: {
      type: String,
    },
    _current: {
      state: true,
    },
    vertical: {
      reflect: true,
    },
    pressed: {
      type: Boolean,
      reflect: true,
    },
    dots: {
      reflect: true,
    },
    _advance: {
      state: true,
    },
    _scrollLeft: {
      state: true,
    },
    _scrollDirection: {
      state: true,
    },
    _direction: {
      state: true,
    },
  }

  $container = createRef()
  $next = createRef()
  $prev = createRef()

  constructor() {
    super()
    this.dragSpeed = 3
    this._scrollLeft = 0
    this.snappingTimeout = undefined
  }

  connectedCallback() {
    super.connectedCallback()

    setTimeout(() => {
      this.toggleDraggableEvents()
    }, 0)

    this.setActive()
    window.addEventListener("resize", () => this.setActive())
  }

  setActive() {
    this.active = this.isAvailable()
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.toggleDraggableEvents("remove")
  }

  toggleDraggableEvents(action = "add") {
    const actionValue =
      action === "add" ? "addEventListener" : "removeEventListener"
    if (this.$container.value) {
      this.$container.value[actionValue](
        "touchstart",
        this.clearTimeoutAndClasses.bind(this)
      )

      this.$container.value[actionValue](
        "mouseleave",
        this.handleMouseLeave.bind(this)
      )

      this.$container.value[actionValue](
        "mousedown",
        this.handleMouseDown.bind(this)
      )

      this.$container.value[actionValue](
        "mousemove",
        this.handleDragStart.bind(this)
      )

      this.$container.value[actionValue](
        "mouseup",
        this.handleDragEnd.bind(this)
      )
    }
  }

  clearTimeoutAndClasses() {
    clearTimeout(this.snappingTimeout)
    this.clearDragClasses()
  }

  handleMouseLeave() {
    this.pressed = false
    this.clearDragClasses()
  }

  handleMouseDown(event) {
    clearTimeout(this.snappingTimeout)
    this.pressed = true
    this.classList.add("has-deactivated-snap")
    this.startX = event.pageX - this.$container.value.offsetLeft
    this._scrollLeft = this.$container.value.scrollLeft
  }

  handleDragStart(event) {
    if (!this.pressed) return
    event.preventDefault()
    const currentX = event.pageX - this.$container.value.offsetLeft
    const slideDistance = (currentX - this.startX) * this.dragSpeed
    this.toggleDragClass(slideDistance)

    this.$container.value.scrollTo({
      left: this._scrollLeft - slideDistance,
    })
  }

  handleDragEnd(event) {
    this.pressed = false
    const oldScrollLeft = this.$container.value.scrollLeft
    this.classList.remove("has-deactivated-snap")
    const newScrollLeft = this.$container.value.scrollLeft
    this.classList.add("has-deactivated-snap")
    this.$container.value.scrollLeft = oldScrollLeft

    this.$container.value.scrollTo({
      left: newScrollLeft,
      behavior: "smooth",
    })
    this.snappingTimeout = setTimeout(this.clearDragClasses.bind(this), 400)
  }

  toggleDragClass(slideDistance) {
    const isDragging =
      (slideDistance < 0 && slideDistance < -50) ||
      (slideDistance > 0 && slideDistance > 50)
    this.classList.toggle("is-dragging", isDragging)
  }

  clearDragClasses() {
    this.classList.remove("has-deactivated-snap")
    this.classList.remove("is-dragging")
  }

  isAvailable() {
    return this.breakpoint ? !window.matchMedia(this.breakpoint).matches : true
  }

  render() {
    return html`
      <div class="container" ${ref(this.$container)} >
        <slot name="item"></slot>
      </div>
      <div class="buttons">
        <div class="counter">
          <span class="counter--current">${this._current}</span>
          <span aria-hidden="true"> / </span>
          <span class="visually-hidden">${t("accessibility.of", "of")}</span>
          <span class="counter--total">${this._total ? this._total : ""}</span>
        </div>
        <button
          ${ref(this.$prev)}
          @click="${this.move}"
          type="button"
          class="button button--prev"
          name="previous"
          aria-label="${t("accessibility.previous_slide", "Previous")}"
          ?disabled="${this._current === 1}"
        >
          ${unsafeHTML(paths.caret)}
        </button>
        <button
          ${ref(this.$next)}
          @click="${this.move}"
          type="button"
          class="button button--next"
          name="next"
          aria-label="${t("accessibility.next_slide", "Next")}"
          ?disabled="${this.__current === this._total}"
        >
          ${unsafeHTML(paths.caret)}
        </button>
      </div>
      ${this.renderDots()}
    `
  }

  static styles = [
    styles.visuallyHidden,
    css`
      *,
      *::before,
      *::after {
        box-sizing: border-box;
      }

      :host {
        display: block;
        position: relative;
      }

      :host([active="true"]) {
        padding-right: var(--right-offset);
        padding-bottom: var(--bottom-offset, 0);
      }

      .container {
        display: flex;
        flex-wrap: nowrap;
        position: relative;
        overflow-y: hidden;
        overflow-x: auto;
        scroll-snap-type: x mandatory;
        scrollbar-width: none;
        -webkit-overflow-scrolling: touch;
        scroll-padding-left: var(--left-offset);
        scroll-padding-right: var(--right-offset);
        padding-left: var(--right-offset);
        margin-inline: -0.2rem;
      }

      :host([active="true"]) .container {
        margin-inline: 0 calc(-1 * var(--right-offset));
        padding-left: var(--right-offset);
        -ms-overflow-style: none; /* Internet Explorer 10+ */
        scrollbar-width: none; /* Firefox */
        scrollbar-height: none; /* Firefox */
      }

      :host([active="true"]) .container::-webkit-scrollbar {
        width: 0;
        height: 0;
        background: transparent; /* Optional: just make scrollbar invisible */
        display: none;
      }

      :host([active="true"].has-deactivated-snap) .container {
        scroll-snap-type: none;
      }

      :host([active="true"][vertical="true"]) .container {
        aspect-ratio: var(--hh-vertical-aspect-ratio);
        flex-wrap: wrap;
        overflow-y: auto;
        overflow-x: hidden;
        scroll-snap-type: y mandatory;
      }

      ::slotted(*) {
        flex: 0 0 var(--slotted-width, 50%);
        max-width: var(--slotted-width, 50%);
        scroll-snap-align: var(--scroll-snap-align, end);
        padding-inline: var(--item-padding-inline, 0 0.4rem);
      }

      :host([active="true"]) ::slotted(*:not(:first-child)) {
        padding-inline: var(--item-padding-inline, 0 0.4rem);
      }

      :host([active="true"]) ::slotted(:last-child) {
        margin-right: var(--right-offset);
      }

      @media screen and (min-width: 750px) {
        ::slotted(*) {
          flex: 0 0 var(--slotted-width, 33.33%);
          max-width: var(--slotted-width, 33.33%);
          scroll-snap-align: var(--scroll-snap-align, start);
        }
      }

      .buttons {
        display: none;
        align-items: center;
        position: absolute;
        z-index: 2;
        right: 0;
        bottom: 0;
        border: var(--hh-button-border);
        background: var(--hh-button-background);
      }

      :host([active="true"][buttons="true"]) .buttons {
        display: flex;
      }

      .dots {
        display: none;
        position: absolute;
        z-index: 3;
        bottom: 0;
        left: var(--left-offset);
        right: var(--right-offset);
      }

      :host([vertical="true"]) .dots {
        top: 50%;
        bottom: auto;
        right: auto;
        left: 10px;
        transform: translateY(-50%);
      }

      :host([active="true"][dots="true"]) .dots {
        display: block;
      }

      .dots ul {
        list-style-type: none;
        display: flex;
        padding: 0;
        margin: 0;
      }

      :host([vertical="true"]) .dots ul {
        display: block;
      }

      .dots li {
        margin: 0;
        flex: 1;
        display: flex;
      }

      :host([vertical="true"]) .dots li {
        margin: 5px 0;
      }

      .dots button {
        background: var(--hh-dots-line-background, #eae6e3);
        width: 100%;
        height: 1px;
        padding: 0;
        border: 0;
        border-radius: 0;
        position: relative;
        z-index: 1;
      }

      .dots button.is-current {
        border-radius: 0;
      }

      .dots button::after {
        content: "";
        position: absolute;
        width: 0;
        height: 100%;
        top: 0;
        left: 0;
        background: var(--hh-dots-line-background-active, #000000);
        transition: width var(--hh-dots-line-duration, 150ms) ease;
      }

      .dots button.active::after {
        width: 100%;
      }

      .counter {
        margin: 0 2rem;
      }

      .button {
        width: var(--hh-button-width, 44px);
        height: var(--hh-button-height, 44px);
        cursor: pointer;
        color: var(--hh-color);
        border: none;
        background: var(--hh-button-background);
      }

      .button svg {
        height: 0.6rem;
      }

      .button--prev {
        margin-left: -0.2rem;
        transform: rotate(90deg);
      }

      .button--next {
        margin-left: -0.2rem;
        transform: rotate(-90deg);
      }
    `,
  ]
}

customElements.define("hh-draggable-slider", HHDraggableSlider)
